import { AxiosResponse } from "axios";
import { getFileNameFromUrl } from "~/user/helpers";
import {
    getExtensionFromMimeType,
    getMimeTypeForExtension,
    getMimeTypeForUrl,
} from "messages/attachmentUtils";

export default class FileTransferService {
    public downloadFilesFromURL(urls: (string)[], folderName: string = '') {
        var allPromise: Promise<string>[] = [];
        for (let i = 0; i < urls.length; i++) {
            allPromise.push(this.downloadFileFromURL(urls[i], folderName));
        }

        return Promise.all(allPromise).then(files => {
            return files;
        });
    }

    public downloadFileFromURL(url: string, folderName: string = '', fileName?: string) {
        const resolvedName = fileName || getFileNameFromUrl(url) || '';
        return new Promise<string>((resolve) => {
            window['resolveLocalFileSystemURL'](this.getFileNameFromURL(url, folderName, resolvedName), () => {
                resolve(folderName ? `${folderName}/${resolvedName}` : resolvedName)
            }, () => {
                console.log('plugin.http.sendRequest');
                const options = { method: 'GET', responseType: 'blob' };
                window['cordova'].plugin.http.sendRequest(url, options, (response) => {
                    if (response.status === 200) {
                        let file: any = response.data instanceof Blob
                            ? response.data
                            : new Blob([response.data], { type: response.headers['content-type'] });
                        file.name = resolvedName;
                        console.log('file.name', file.name);
                        file.lastModified = response.headers['last-modified'];
                        this.saveFileFromBlob(file, folderName).then((path) => resolve(folderName ? `${folderName}/${file.name}` : file.name));
                    } else {
                        resolve('');
                    }
                }, () => resolve(''));
            });
        });
    }

    public getFileNameFromURL(url: string, folderName: string = '', fileName?: string) {
        const name = fileName || url.split('?')[0].split('/').pop();
        return window['cordova'].file.dataDirectory + (folderName ? folderName + '/' : '') + name;
    }

    public getFolderPath(cacheDirectory = false) {
        return cacheDirectory ? window['cordova'].file.cacheDirectory : window['cordova'].file.dataDirectory;
    }

    public saveFilesFromBlobs(blobFile: File[] = [], folderName: string = 'temporary') {
        var allPromise: Promise<string>[] = [];
        for (let i = 0; i < blobFile.length; i++) {
            allPromise.push(this.saveFileFromBlob(blobFile[i], folderName));
        }

        return Promise.all(allPromise).then(files => {
            return files.map(path => `${folderName}/${getFileNameFromUrl(path)}`);
        });
    }

    public createFile(directory, blobFile: File, resolve) {
        directory.getFile(blobFile.name, { create: true, exclusive: false }, (fileEntry) => {
            fileEntry.createWriter((fileWriter) => {
                fileWriter.onwriteend = () => resolve(window['WkWebView'] ? window['WkWebView'].convertFilePath(fileEntry.toURL()) : (fileEntry.nativeURL || fileEntry.toURL()));
                fileWriter.onerror = () => resolve('');

                if (typeof FileReader !== 'undefined' && blobFile instanceof Blob) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        fileWriter.write(reader.result);
                    };
                    reader.onerror = () => resolve('');
                    reader.readAsArrayBuffer(blobFile);
                } else {
                    fileWriter.write(blobFile);
                }
            });
        }, () => resolve(''));
    }

    public createDirectory(directory, blobFile: File, folderName: string = '', resolve) {
        if (folderName !== '') {
            directory.getDirectory(folderName.split("/")[0], { create: true }, (subdirectory) => {
                this.createDirectory(subdirectory, blobFile, folderName.split('/').slice(1).join('/'), resolve);
            }, () => resolve(''));
        } else {
            this.createFile(directory, blobFile, resolve);
        }
    }

    public removeFolder(folderUrl: string) {
        const uri = `${this.getFolderPath()}/${folderUrl}`;
        window['resolveLocalFileSystemURL'](uri, (dirEntry) => {
            dirEntry.removeRecursively(
                () => console.log('successfully deleted the folder and its content'),
                e => console.error('there was an error deleting the directory', e.toString())
            )
        });
    }

    public saveFileFromBlob(blobFile: File, folderName: string = '', cacheDirectory: boolean = false) {
        return new Promise<string>((resolve) => {
            window['resolveLocalFileSystemURL'](this.getFolderPath(cacheDirectory), (directory) => {
                this.createDirectory(directory, blobFile, folderName, resolve);
            }, () => resolve(''));
        });
    }

    public getBlobsFromURLs(files: string[] = []) {
        var allPromise: Promise<File>[] = [];
        for (let i = 0; i < files.length; i++) {
            allPromise.push(this.getBlobFromURL(`${window['cordova'].file.dataDirectory}/${files[i]}`));
        }

        return Promise.all(allPromise).then(files => {
            return files;
        });
    }

    public getBlobFromURL(url: string) {
        return new Promise<File>((resolve) => {
            window['resolveLocalFileSystemURL'](url,
                (fileEntry) => {
                    fileEntry.file((file) => {
                        let reader = new FileReader();
                        reader.readAsArrayBuffer(file);
                        reader.onload = () => {
                            if (reader.result) {
                                let fileObj: any = new Blob([reader.result], { type: file.type });
                                fileObj.name = file.name;
                                fileObj.lastModified = file.lastModified;
                                resolve(fileObj);
                            }
                            resolve();
                        }
                        reader.onerror = () => resolve();
                    }, () => resolve());
                }, () => resolve())
        });
    }

    public removeFiles(files: string[] = []) {
        var allPromise: Promise<void>[] = [];
        for (let i = 0; i < files.length; i++) {
            allPromise.push(this.removeFile(`${window['cordova'].file.dataDirectory}/${files[i]}`));
        }

        return Promise.all(allPromise).then(files => {
            return files;
        });
    }

    public removeFile(url: string) {
        return new Promise<void>((resolve) => {
            const filePath = url.substr(0, url.lastIndexOf('/'));
            const fileName = url.substr(url.lastIndexOf('/') + 1);
            window['resolveLocalFileSystemURL'](filePath, (directory) => {
                directory.getDirectory(fileName, { create: true }, (fileEntry) => {
                    fileEntry.remove(() => resolve(), (error) => resolve(), () => resolve());
                }, () => resolve());
            }, () => resolve());
        });
    }

    public isFileExist(url: string, folderName: string = '') {
        if (window['cordova']) {
            return new Promise<string>((resolve) => {
                window['resolveLocalFileSystemURL'](this.getFileNameFromURL(url, folderName), () => {
                    resolve(this.getFileNameFromURL(url, folderName))
                }, () => resolve(''));
            });
        } else {
            return '';
        }
    }

    public openFile(path: string, mimeType: string = '') {
        return new Promise<void>((resolve, reject) => {
            if (window['cordova']) {
                window['cordova'].plugins.fileOpener2.open(path, mimeType, {
                    error: (e) => {
                        console.log('Error status: ' + e.status + ' - Error message: ' + e.message);
                        reject(e);
                    },
                    success: () => resolve()
                });
            } else {
                window.open(path, '_blank');
                resolve();
            }
        });
    }

    public getMimeTypeFromUrl(url: string = '', fileName?: string) {
        const name = fileName || getFileNameFromUrl(url) || '';
        const extension = name.split('.').pop()?.toLowerCase();
        return (extension && getMimeTypeForExtension(extension)) || getMimeTypeForUrl(url);
    }

    private sanitizeFileName(fileName: string = '') {
        const cleaned = (fileName || 'attachment').split(/[/\\]/).pop() || 'attachment';
        // eslint-disable-next-line no-control-regex
        return cleaned.replace(/[<>:"|?*\x00-\x1F]/g, '_').trim() || 'attachment';
    }

    private getExtensionFromMimeType(mimeType: string = '') {
        return getExtensionFromMimeType(mimeType, false);
    }

    private resolveDownloadFileName(fileName?: string, url: string = '', mimeType?: string) {
        let name = this.sanitizeFileName(fileName || getFileNameFromUrl(url) || 'attachment');
        if (!/\.[a-z0-9]+$/i.test(name)) {
            const extension = this.getExtensionFromMimeType(mimeType)
                || (this.getMimeTypeFromUrl(url, name) !== 'application/octet-stream'
                    ? (getFileNameFromUrl(url) || '').split('.').pop()
                    : '');
            if (extension) {
                name = `${name}.${extension}`;
            }
        }
        return name;
    }

    private arrayBufferToBase64(buffer: ArrayBuffer) {
        const bytes = new Uint8Array(buffer);
        const chunkSize = 0x8000;
        let binary = '';
        for (let i = 0; i < bytes.length; i += chunkSize) {
            binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)) as number[]);
        }
        return window.btoa(binary);
    }

    private fetchUrlAsBase64(url: string): Promise<string> {
        return new Promise((resolve, reject) => {
            window['cordova'].plugin.http.sendRequest(url, {
                method: 'GET',
                responseType: 'arraybuffer'
            }, (response) => {
                if (response.status < 200 || response.status >= 300 || !response.data) {
                    reject(new Error('Download failed'));
                    return;
                }
                try {
                    resolve(this.arrayBufferToBase64(response.data));
                } catch (error) {
                    reject(error);
                }
            }, (error) => reject(error || new Error('Download failed')));
        });
    }

    private ensureIosDownloadsDirectory(): Promise<string> {
        const documentsDirectory = window['cordova']?.file?.documentsDirectory;
        if (!documentsDirectory) {
            return Promise.reject(new Error('Documents directory is not available'));
        }

        return new Promise((resolve, reject) => {
            window['resolveLocalFileSystemURL'](documentsDirectory, (rootDirectory) => {
                rootDirectory.getDirectory('Downloads', { create: true }, (downloadsDirectory) => {
                    resolve(downloadsDirectory.nativeURL || downloadsDirectory.toURL() || (documentsDirectory + 'Downloads/'));
                }, () => reject(new Error('Failed to create Downloads folder')));
            }, () => reject(new Error('Failed to open documents directory')));
        });
    }

    private downloadFileNative(url: string, filePath: string): Promise<string> {
        return new Promise((resolve, reject) => {
            window['cordova'].plugin.http.downloadFile(
                url,
                {},
                {},
                filePath,
                (entry) => resolve(entry.nativeURL || entry.toURL() || filePath),
                (error) => reject(error || new Error('Download failed'))
            );
        });
    }

    public downloadToDeviceStorage(
        url: string,
        options: {
            mimeType?: string
            fileName?: string
        } = {}
    ) {
        const fileName = this.resolveDownloadFileName(options.fileName, url, options.mimeType);
        const platformId = window['cordova']?.platformId;

        if (platformId === 'ios') {
            return this.ensureIosDownloadsDirectory().then((downloadsDir) => {
                const separator = downloadsDir.endsWith('/') ? '' : '/';
                const filePath = `${downloadsDir}${separator}${fileName}`;
                return this.downloadFileNative(url, filePath).then((savedPath) => savedPath);
            });
        }

        // Android: MediaStore Downloads requires base64 for saf-mediastore
        return this.fetchUrlAsBase64(url).then((base64) => {
            const safMediastore = window['cordova']?.plugins?.safMediastore;
            if (!safMediastore?.writeFile) {
                return Promise.reject(new Error('Storage plugin is not available'));
            }

            return safMediastore.writeFile({
                data: base64,
                filename: fileName
            }).then((savedPath) => savedPath);
        });
    }

    public downloadAndOpenFile(
        url: string,
        _folderName: string = 'downloads',
        options: { mimeType?: string; fileName?: string } = {}
    ) {
        return this.downloadToDeviceStorage(url, options);
    }

    public saveFileFromBlobResponse(response: AxiosResponse, url: string, folderName: string = '') {
        let file: any = new Blob([response.data], { type: response.headers['content-type'] });
        file.name = url.split('?')[0].split('/').pop();
        file.lastModified = response.headers['last-modified'];
        return this.saveFileFromBlob(file, folderName).then((path) => {
            return path;
        });
    }
}
